import type * as Immutable from 'immutable'
import { Button, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import { LibraryAddRounded as LibraryAddRoundedIcon, MoreVert as MoreVertIcon, RemoveCircleOutlineRounded as RemoveCircleOutlineRoundedIcon, AddBusinessRounded as AddBusinessRoundedIcon } from '@mui/icons-material'
import { useCallback, useMemo, useState } from 'react'
import type { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import DeleteIcon from '../../Common/Icons/DeleteIcon'
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch'
import { useAppSelector } from '../../Common/_hooks/useAppSelector'
import { ClearProductDesignSelectedProduct, SelectAllProductDesignSelectedProduct } from '../../UIData/_actions/Account/AccountStoreActions'
import type { BatchProductDesignSelection } from '../../UserData/_actions/ProductDesignActions'
import type { ProductSearchParams } from './ProductSearchBar'
import type { CollectionStore } from '../../UserData/_stores/CollectionStore'
import AddToCollectionDialog from '../Collections/AddToCollectionDialog'
import DeleteDesignDialog from './DeleteDesignDialog'
import RemoveFromCollectionDialog from './RemoveFromCollectionDialog'
import BatchSelectionActionBar from '../BatchSelectionActionBar'
import PublishSelectedProductDesignsDialog from './PublishSelectedProductDesignsDialog'
import getStoreFeatures from '../../Common/_hooks/Store/getStoreFeatures'

interface Props {
  listId: string
  search: ProductSearchParams
  selectedCollection?: CollectionStore
}

interface DialogBatchSelection {
  selection: BatchProductDesignSelection
  selectionCount: number
  totalMatchingCount: number
}

const buildBatchSelection = (
  productDesignSelectedProducts: Immutable.OrderedSet<number>,
  productDesignSelectedAllProducts: boolean,
  productDesignExcludedSelectedProducts: Immutable.OrderedSet<number>,
  search: ProductSearchParams,
): BatchProductDesignSelection => ({
  ids: productDesignSelectedProducts.toArray(),
  selectAll: productDesignSelectedAllProducts,
  excludeIds: productDesignExcludedSelectedProducts.toArray(),
  filters: {
    search: search.search,
    categories: search.categories,
    collections: search.collections,
  },
})

export default function ProductDesignListActions(props: Props) {
  const dispatch = useAppDispatch()
  const [t] = useTranslation('account-v2')

  const productDesignSelectedProducts = useAppSelector(state => state.get('UIData').get('account').get('productDesignSelectedProducts'))
  const productDesignSelectedAllProducts = useAppSelector(state => state.get('UIData').get('account').get('productDesignSelectedAllProducts'))
  const productDesignExcludedSelectedProducts = useAppSelector(state => state.get('UIData').get('account').get('productDesignExcludedSelectedProducts'))
  const hasPublishableStore = useAppSelector(state => state.get('userData').get('stores').some(store => (
    getStoreFeatures(store.get('slug')).canCreateProduct
  )))
  const totalMatchingProducts = useAppSelector(state =>
    state.get('userData')
      .get('productDesigns')
      .get('itemLists')
      .get(props.listId)
      ?.get('total') ?? 0)

  const [addToCollectionDialogOpened, setAddToCollectionDialogOpened] = useState(false)
  const [publishSelectedProductDesignsDialogOpened, setPublishSelectedProductDesignsDialogOpened] = useState(false)
  const [removeFromCollectionDialogOpened, setRemoveFromCollectionDialogOpened] = useState(false)
  const [deleteDialogOpened, setDeleteDialogOpened] = useState(false)
  const [dialogBatchSelection, setDialogBatchSelection] = useState<DialogBatchSelection | null>(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)

  const selectedCount = useMemo(() => {
    if (productDesignSelectedAllProducts) {
      return Math.max(totalMatchingProducts - productDesignExcludedSelectedProducts.count(), 0)
    }

    return productDesignSelectedProducts.count()
  }, [productDesignExcludedSelectedProducts, productDesignSelectedAllProducts, productDesignSelectedProducts, totalMatchingProducts])

  const remainingToSelect = useMemo(() => {
    return Math.max(totalMatchingProducts - selectedCount, 0)
  }, [selectedCount, totalMatchingProducts])

  const getBatchSelection = useCallback((): BatchProductDesignSelection => (
    buildBatchSelection(
      productDesignSelectedProducts,
      productDesignSelectedAllProducts,
      productDesignExcludedSelectedProducts,
      props.search,
    )
  ), [productDesignExcludedSelectedProducts, productDesignSelectedAllProducts, productDesignSelectedProducts, props.search])

  const captureDialogBatchSelection = useCallback(() => {
    setDialogBatchSelection({
      selection: getBatchSelection(),
      selectionCount: selectedCount,
      totalMatchingCount: totalMatchingProducts,
    })
  }, [getBatchSelection, selectedCount, totalMatchingProducts])

  const clearDialogBatchSelection = useCallback(() => {
    setDialogBatchSelection(null)
  }, [])

  const onClear = useCallback(() => {
    dispatch(ClearProductDesignSelectedProduct())
  }, [dispatch])

  const onSelectAll = useCallback(() => {
    dispatch(SelectAllProductDesignSelectedProduct())
  }, [dispatch])

  const onMenuOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget)
  }, [])

  const onMenuClose = useCallback(() => {
    setMenuAnchorEl(null)
  }, [])

  const onAddToCollection = useCallback(() => {
    onMenuClose()
    captureDialogBatchSelection()
    setAddToCollectionDialogOpened(true)
  }, [captureDialogBatchSelection, onMenuClose])

  const onAddToStore = useCallback(() => {
    captureDialogBatchSelection()
    setPublishSelectedProductDesignsDialogOpened(true)
  }, [captureDialogBatchSelection])

  const onRemoveFromCollection = useCallback(() => {
    captureDialogBatchSelection()
    setRemoveFromCollectionDialogOpened(true)
  }, [captureDialogBatchSelection])

  const onDelete = useCallback(() => {
    onMenuClose()
    captureDialogBatchSelection()
    setDeleteDialogOpened(true)
  }, [captureDialogBatchSelection, onMenuClose])

  const onAddToCollectionDialogClose = useCallback(() => {
    setAddToCollectionDialogOpened(false)
    clearDialogBatchSelection()
  }, [clearDialogBatchSelection])

  const onPublishSelectedProductDesignsDialogClose = useCallback(() => {
    setPublishSelectedProductDesignsDialogOpened(false)
    clearDialogBatchSelection()
  }, [clearDialogBatchSelection])

  const onRemoveFromCollectionDialogClose = useCallback(() => {
    setRemoveFromCollectionDialogOpened(false)
    clearDialogBatchSelection()
  }, [clearDialogBatchSelection])

  const onDeleteDialogClose = useCallback(() => {
    setDeleteDialogOpened(false)
    clearDialogBatchSelection()
  }, [clearDialogBatchSelection])

  return <>
    <BatchSelectionActionBar
      selectedCount={selectedCount}
      totalMatchingCount={totalMatchingProducts}
      selectAllActive={productDesignSelectedAllProducts}
      onClear={onClear}
      onSelectAll={remainingToSelect > 0 ? onSelectAll : undefined}
      actions={<>
        {hasPublishableStore ? <Button
          color="primary"
          startIcon={<AddBusinessRoundedIcon />}
          onClick={onAddToStore}
        >{t('Add to store')}</Button> : null}
        {props.selectedCollection ? <Button
          color="secondary"
          startIcon={<RemoveCircleOutlineRoundedIcon />}
          onClick={onRemoveFromCollection}
          sx={{
            display: {
              xs: 'none',
              lg: 'inline-flex',
            }
          }}
        >{t('Remove from collection')}</Button> : null}
        <IconButton
          color="secondary"
          onClick={onMenuOpen}
          aria-label={t('More')}
        >
          <MoreVertIcon />
        </IconButton>
      </>}
    />

    <Menu
      anchorEl={menuAnchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      open={!!menuAnchorEl}
      onClose={onMenuClose}
    >
      <MenuItem
        onClick={onAddToCollection}
        sx={{
          color: 'secondary.main',
          '& .MuiListItemIcon-root': {
            color: 'secondary.main',
          },
        }}
      >
        <ListItemIcon><LibraryAddRoundedIcon /></ListItemIcon>
        <ListItemText>{t('Add to collection')}</ListItemText>
      </MenuItem>
      {props.selectedCollection ? <MenuItem
        color="secondary"
        onClick={onRemoveFromCollection}
        sx={{
          display: {
            xs: 'inline-flex',
            lg: 'none',
          }
        }}
      >
        <ListItemIcon><RemoveCircleOutlineRoundedIcon /></ListItemIcon>
        <ListItemText>{t('Remove from collection')}</ListItemText>
      </MenuItem> : null}
      <MenuItem
        onClick={onDelete}
        sx={{
          color: 'error.main',
          '& .MuiListItemIcon-root': {
            color: 'error.main',
          },
        }}
      >
        <ListItemIcon><DeleteIcon /></ListItemIcon>
        <ListItemText>{t('Delete')}</ListItemText>
      </MenuItem>
    </Menu>

    {addToCollectionDialogOpened && dialogBatchSelection ? <AddToCollectionDialog
      opened={addToCollectionDialogOpened}
      selection={dialogBatchSelection.selection}
      selectionCount={dialogBatchSelection.selectionCount}
      onClose={onAddToCollectionDialogClose}
    /> : null}

    {publishSelectedProductDesignsDialogOpened && dialogBatchSelection ? <PublishSelectedProductDesignsDialog
      opened={publishSelectedProductDesignsDialogOpened}
      selection={dialogBatchSelection.selection}
      selectionCount={dialogBatchSelection.selectionCount}
      totalMatchingCount={dialogBatchSelection.totalMatchingCount}
      onClose={onPublishSelectedProductDesignsDialogClose}
    /> : null}

    {removeFromCollectionDialogOpened && dialogBatchSelection && props.selectedCollection ? <RemoveFromCollectionDialog
      opened={removeFromCollectionDialogOpened}
      collectionId={props.selectedCollection.get('id')}
      collectionName={props.selectedCollection.get('name')}
      selection={dialogBatchSelection.selection}
      selectionCount={dialogBatchSelection.selectionCount}
      onClose={onRemoveFromCollectionDialogClose}
    /> : null}

    {deleteDialogOpened && dialogBatchSelection ? <DeleteDesignDialog
      opened={deleteDialogOpened}
      selection={dialogBatchSelection.selection}
      selectionCount={dialogBatchSelection.selectionCount}
      onClose={onDeleteDialogClose}
    /> : null}
  </>
}
